import { useState } from 'react'
import type { MouseEvent } from 'react'
import type { PuzzleRendererProps } from '@/game/PuzzleRenderer'

type Config = { image?: { url: string; alt?: string }; instructions?: string }
type Point = { x: number; y: number }

/**
 * Tap a spot on the image. Coordinates are normalised (0..1) against the
 * rendered image box, so resizing/rotating the device never changes the answer.
 */
export function HotspotPuzzle({ puzzle, onSubmit, disabled }: PuzzleRendererProps) {
  const config = puzzle.publicConfig as Config
  const [point, setPoint] = useState<Point | null>(null)

  function place(e: MouseEvent<HTMLImageElement>) {
    if (disabled) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height))
    setPoint({ x, y })
  }

  if (!config.image) return <p className="status-err">This puzzle has no image configured.</p>

  return (
    <div className="puzzle-form">
      {config.instructions && <p className="muted">{config.instructions}</p>}
      <div className="hotspot">
        <img src={config.image.url} alt={config.image.alt ?? ''} onClick={place} draggable={false} />
        {point && (
          <span className="hotspot-marker" style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }} aria-hidden="true" />
        )}
      </div>
      <p className="muted" role="status">
        {point ? 'Marker placed. Tap again to move it.' : 'Tap the image to place your marker.'}
      </p>
      <button
        type="button"
        className="primary"
        disabled={disabled || !point}
        onClick={() => point && void onSubmit({ x: Number(point.x.toFixed(4)), y: Number(point.y.toFixed(4)) })}
      >
        {disabled ? 'Checking…' : 'Submit location'}
      </button>
    </div>
  )
}
